/**
 * Reading a bank statement CSV into rows the import can act on.
 *
 * Every bank lays its export out differently — one amount column or separate
 * paid in / paid out, dates as 01/10/2026 or 01 Oct 2026 or 2026-10-01, a
 * preamble before the header or none. The layouts below cover the online and
 * high-street banks people are most likely to have; anything else falls back
 * to asking which column is which (see `ColumnMapping`).
 */

export interface StatementRow {
  date: string // ISO yyyy-mm-dd
  description: string
  amount: number // signed: +in, -out
  /** The running balance after this row, when the statement has one. */
  balance?: number
  /**
   * The counterparty on its own, where the bank exports one (Monzo's Name,
   * Starling's Counter Party): the cleanest source of a merchant's real name.
   */
  payee?: string
  /**
   * The merchant's own part of the description, where the statement keeps it
   * apart from the reference or town (an HSBC PDF's first line). Merchants are
   * matched on it, so "TESCO STORES 3125 ST NEOTS" is still TESCO STORES.
   */
  merchantText?: string
  /**
   * The bank's own kind of payment, where it exports one (Starling's and
   * NatWest's Type, Lloyds' Transaction Type): "DIRECT DEBIT", "D/D", "SO".
   * How a single payment shows itself as a regular one (SM-30).
   */
  type?: string
}

export type DateFormat = 'dmy' | 'ymd' | 'mdy'

/** Which column holds what. Indices into the header row. */
export interface ColumnMapping {
  date: number
  /** Joined with a space when there's more than one (Starling's counterparty and reference). */
  description: number[]
  /** A single signed column… */
  amount?: number
  /** …or separate unsigned columns for money in and out. */
  paidIn?: number
  paidOut?: number
  balance?: number
  payee?: number
  type?: number
  dateFormat: DateFormat
}

export interface BankLayout {
  name: string
  /** Every one of these headers must be present (case-insensitive) for the layout to match. */
  requires: string[]
  map: (find: (header: string) => number) => Omit<ColumnMapping, 'dateFormat'> & { dateFormat?: DateFormat }
}

export const BANK_LAYOUTS: BankLayout[] = [
  {
    name: 'Monzo',
    requires: ['Transaction ID', 'Name', 'Amount', 'Money Out', 'Money In'],
    map: (f) => ({ date: f('Date'), description: [f('Name')], amount: f('Amount'), payee: f('Name'), type: f('Type') }),
  },
  {
    name: 'Starling',
    requires: ['Counter Party', 'Reference', 'Amount (GBP)'],
    map: (f) => ({
      date: f('Date'),
      description: [f('Counter Party'), f('Reference')],
      payee: f('Counter Party'),
      type: f('Type'),
      amount: f('Amount (GBP)'),
      balance: f('Balance (GBP)'),
    }),
  },
  {
    name: 'Revolut',
    requires: ['Started Date', 'Completed Date', 'Description', 'Amount', 'Fee'],
    map: (f) => ({ date: f('Completed Date'), description: [f('Description')], amount: f('Amount'), balance: f('Balance'), dateFormat: 'ymd' }),
  },
  {
    name: 'Chase UK',
    requires: ['Transaction Date', 'Transaction Description', 'Amount', 'Balance'],
    map: (f) => ({ date: f('Transaction Date'), description: [f('Transaction Description')], amount: f('Amount'), balance: f('Balance') }),
  },
  {
    // Halifax and Bank of Scotland export the same layout.
    name: 'Lloyds / Halifax',
    requires: ['Transaction Date', 'Transaction Description', 'Debit Amount', 'Credit Amount'],
    map: (f) => ({
      date: f('Transaction Date'),
      description: [f('Transaction Description')],
      paidOut: f('Debit Amount'),
      paidIn: f('Credit Amount'),
      balance: f('Balance'),
      type: f('Transaction Type'),
    }),
  },
  {
    name: 'Nationwide',
    requires: ['Date', 'Transaction type', 'Description', 'Paid out', 'Paid in', 'Balance'],
    map: (f) => ({
      date: f('Date'),
      description: [f('Description')],
      paidOut: f('Paid out'),
      paidIn: f('Paid in'),
      balance: f('Balance'),
      type: f('Transaction type'),
    }),
  },
  {
    // RBS and Ulster export the same layout.
    name: 'NatWest / RBS',
    requires: ['Date', 'Type', 'Description', 'Value', 'Balance', 'Account Name'],
    map: (f) => ({ date: f('Date'), description: [f('Description')], amount: f('Value'), balance: f('Balance'), type: f('Type') }),
  },
  {
    name: 'Barclays',
    requires: ['Number', 'Date', 'Account', 'Amount', 'Subcategory', 'Memo'],
    map: (f) => ({ date: f('Date'), description: [f('Memo')], amount: f('Amount'), type: f('Subcategory') }),
  },
  {
    name: 'Santander',
    requires: ['Date', 'Description', 'Money in', 'Money out', 'Balance'],
    map: (f) => ({ date: f('Date'), description: [f('Description')], paidIn: f('Money in'), paidOut: f('Money out'), balance: f('Balance') }),
  },
  {
    name: 'First Direct / HSBC',
    requires: ['Date', 'Description', 'Amount'],
    map: (f) => ({ date: f('Date'), description: [f('Description')], amount: f('Amount'), balance: f('Balance') }),
  },
]

/** RFC 4180-ish: quoted fields, doubled quotes inside them, CRLF or LF, a BOM at the start. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  const src = text.replace(/^\uFEFF/, '')

  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"'
          i++
        } else quoted = false
      } else field += c
    } else if (c === '"') quoted = true
    else if (c === ',') {
      row.push(field)
      field = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else field += c
  }
  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows.map((r) => r.map((f) => f.trim())).filter((r) => r.some((f) => f !== ''))
}

export interface ParsedStatement {
  headers: string[]
  rows: string[][]
}

/**
 * Finds the header row. Some banks (Nationwide) put the account name and
 * balance above it, so it's the first row that looks like one: three or more
 * cells, one of them naming a date.
 */
export function splitStatement(cells: string[][]): ParsedStatement | null {
  const at = cells.findIndex((r) => r.length >= 3 && r.some((c) => /date/i.test(c)))
  if (at === -1) return null
  return { headers: cells[at], rows: cells.slice(at + 1).filter((r) => r.length >= 2) }
}

/** The known layout whose headers are all present, and its mapping. */
export function detectLayout(statement: ParsedStatement): { bank: string; mapping: ColumnMapping } | null {
  const lower = statement.headers.map((h) => h.toLowerCase())
  const find = (h: string) => lower.indexOf(h.toLowerCase())
  for (const layout of BANK_LAYOUTS) {
    if (!layout.requires.every((h) => find(h) !== -1)) continue
    const m = layout.map(find)
    const mapping: ColumnMapping = {
      ...m,
      // An optional column the layout names but this file lacks (no Balance on
      // some HSBC exports) is simply absent, not column -1.
      balance: m.balance !== undefined && m.balance >= 0 ? m.balance : undefined,
      type: m.type !== undefined && m.type >= 0 ? m.type : undefined,
      dateFormat: m.dateFormat ?? guessDateFormat(statement.rows.map((r) => r[m.date] ?? '')),
    }
    return { bank: layout.name, mapping }
  }
  return null
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

/**
 * UK banks write day first, so that's the default. Year first is recognised
 * by its shape; month first only when some value can't be day first (a "13"
 * or more in the middle position).
 */
export function guessDateFormat(samples: string[]): DateFormat {
  const parts = samples.map((s) => s.trim().split(/[\s/.-]+/)).filter((p) => p.length >= 3)
  if (parts.some((p) => /^\d{4}$/.test(p[0]))) return 'ymd'
  if (parts.some((p) => Number(p[1]) > 12) && parts.every((p) => Number(p[0]) <= 12)) return 'mdy'
  return 'dmy'
}

/** A statement date as ISO, or null if it can't be read. Accepts "01 Oct 2026" whatever the format. */
export function parseDate(raw: string, format: DateFormat): string | null {
  const s = raw.trim().replace(/[T ]\d{1,2}:\d{2}(:\d{2})?.*$/, '')
  const p = s.split(/[\s/.-]+/)
  if (p.length < 3) return null

  let y: number, m: number, d: number
  const named = MONTHS.indexOf(p[1].slice(0, 3).toLowerCase())
  if (named !== -1) {
    d = Number(p[0])
    m = named + 1
    y = Number(p[2])
  } else if (format === 'ymd') {
    ;[y, m, d] = p.map(Number)
  } else if (format === 'mdy') {
    ;[m, d, y] = p.map(Number)
  } else {
    ;[d, m, y] = p.map(Number)
  }
  if (y < 100) y += 2000
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  const date = new Date(y, m - 1, d)
  if (date.getMonth() !== m - 1) return null // 31 Feb
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** "£1,234.56", "-12.00", "(12.00)", "12.00 CR" — or null for an empty cell. */
export function parseAmount(raw: string | undefined): number | null {
  if (raw === undefined) return null
  let s = raw.trim()
  if (s === '') return null
  let sign = 1
  if (/^\(.*\)$/.test(s)) {
    sign = -1
    s = s.slice(1, -1)
  }
  if (/\s*DR$/i.test(s)) {
    sign = -1
    s = s.replace(/\s*DR$/i, '')
  }
  s = s.replace(/\s*CR$/i, '')
  const n = Number(s.replace(/[£$€,\s]/g, ''))
  return Number.isFinite(n) ? sign * n : null
}

export interface ReadResult {
  rows: StatementRow[]
  /** Rows that couldn't be read: no date, or no amount. Shown so nothing goes missing silently. */
  unreadable: number
  /** The account the statement is for, where it says (SM-25). */
  account?: AccountDetails
}

/**
 * A statement's sort code and account number, digits only. Only ever sent to
 * the server to be fingerprinted; never kept, and never on a row.
 */
export interface AccountDetails {
  sortCode: string
  accountNumber: string
}

/** Six digits of sort code; six to ten of account number, the shorter ones padded to eight as banks do. */
export function accountDetails(sortCode: string, accountNumber: string): AccountDetails | null {
  // A leading apostrophe is how a spreadsheet was told to keep the zeros.
  const sort = sortCode.trim().replace(/^'/, '')
  const number = accountNumber.trim().replace(/^'/, '')
  if (!/^\d{2}[- ]?\d{2}[- ]?\d{2}$/.test(sort) || !/^\d{6,10}$/.test(number)) return null
  return { sortCode: sort.replace(/\D/g, ''), accountNumber: number.padStart(8, '0') }
}

/** "20-32-06 13152170", as Barclays writes both in its Account column. */
const SORT_CODE_AND_NUMBER = /^'?(\d{2}-?\d{2}-?\d{2})[\s-]+(\d{6,10})$/

/**
 * The account a CSV is for, from Sort Code and Account Number columns or an
 * Account column holding both. Null unless every row that has them agrees.
 */
export function csvAccountDetails(statement: ParsedStatement): AccountDetails | null {
  const lower = statement.headers.map((h) => h.toLowerCase().trim())
  const sortAt = lower.indexOf('sort code')
  const numberAt = lower.indexOf('account number')
  const bothAt = [numberAt, lower.indexOf('account')].filter((i) => i !== -1)
  const found = new Map<string, AccountDetails>()
  for (const cells of statement.rows) {
    let details: AccountDetails | null = null
    if (sortAt !== -1 && numberAt !== -1) details = accountDetails(cells[sortAt] ?? '', cells[numberAt] ?? '')
    for (const i of bothAt) {
      const both = SORT_CODE_AND_NUMBER.exec((cells[i] ?? '').trim())
      if (!details && both) details = accountDetails(both[1], both[2])
    }
    if (details) found.set(`${details.sortCode}:${details.accountNumber}`, details)
  }
  return found.size === 1 ? [...found.values()][0] : null
}

export function readRows(statement: ParsedStatement, mapping: ColumnMapping): ReadResult {
  const rows: StatementRow[] = []
  let unreadable = 0
  for (const cells of statement.rows) {
    const date = parseDate(cells[mapping.date] ?? '', mapping.dateFormat)
    let amount: number | null
    if (mapping.amount !== undefined) {
      amount = parseAmount(cells[mapping.amount])
    } else {
      const inn = parseAmount(cells[mapping.paidIn ?? -1]) ?? 0
      const out = parseAmount(cells[mapping.paidOut ?? -1]) ?? 0
      const any = (cells[mapping.paidIn ?? -1] ?? '') !== '' || (cells[mapping.paidOut ?? -1] ?? '') !== ''
      amount = any ? Math.abs(inn) - Math.abs(out) : null
    }
    if (!date || amount === null) {
      unreadable++
      continue
    }
    const description = mapping.description
      .map((i) => cells[i] ?? '')
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
    const balance = mapping.balance !== undefined ? parseAmount(cells[mapping.balance]) : null
    const payee = mapping.payee !== undefined ? (cells[mapping.payee] ?? '').replace(/\s+/g, ' ').trim() : ''
    const type = mapping.type !== undefined ? (cells[mapping.type] ?? '').trim() : ''
    rows.push({
      date,
      description: description || 'Unnamed transaction',
      amount: round2(amount),
      ...(balance !== null ? { balance: round2(balance) } : {}),
      ...(payee ? { payee } : {}),
      ...(type ? { type } : {}),
    })
  }
  // Statements come newest-first or oldest-first; the import works oldest-first.
  // Flipping a newest-first file before the (stable) sort keeps same-day rows
  // in the order they happened, so the last one's balance is the day's close.
  if (rows.length > 1 && rows[0].date > rows[rows.length - 1].date) rows.reverse()
  rows.sort((a, b) => a.date.localeCompare(b.date))
  const account = csvAccountDetails(statement)
  return { rows, unreadable, ...(account ? { account } : {}) }
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * What identifies a merchant across statements: "TESCO STORES 3125 LONDON"
 * and "Tesco Stores 4410" are both TESCO STORES. Digits, card and reference
 * noise and the punctuation between them go; the first few words stay.
 */
export function merchantKey(description: string): string {
  return description
    .toUpperCase()
    .replace(/\b(CARD PAYMENT TO|PAYMENT TO|DIRECT DEBIT TO|DD|SO|FPO|FPI|BGC|POS|VIS|CPT|CONTACTLESS|ON \d.*)\b/g, ' ')
    .replace(/[*#]/g, ' ')
    .replace(/\b\S*\d\S*\b/g, ' ')
    .replace(/[^A-Z&' ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    // Starling joins the counterparty to a reference that often repeats it
    // ("Tesco TESCO STORES 3125"); a word straight after itself is noise.
    .filter((w, i, all) => w !== all[i - 1])
    .slice(0, 3)
    .join(' ')
}

/**
 * An id for a statement row that survives re-importing the same file, or an
 * overlapping one: same date, amount and description. The occurrence count
 * keeps two identical coffees on one day apart.
 */
export function importIds(rows: StatementRow[]): string[] {
  const seen = new Map<string, number>()
  return rows.map((r) => {
    const base = `${r.date}|${r.amount.toFixed(2)}|${r.description.toUpperCase()}`
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    return `${base}|${n}`
  })
}
